import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Download } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatCents, formatDate, todayISO } from '@/lib/utils';
import { linkClass } from '@/components/ExportButtons';
import { SupplierEditModal } from '@/components/SupplierEditModal';
import { useClientSort, type SortValue } from '@/hooks/useClientSort';
import { SortableTh } from '@/components/SortableTh';
import type { Supplier } from '@billflow/shared';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SupplierResponse {
  data: { supplier: Supplier };
}

interface LedgerPO {
  id: number;
  number: string;
  status: string;
  order_date: string;
  total: number;
  amount_paid: number;
}

interface LedgerPayment {
  id: number;
  po_id: number;
  po_number: string;
  amount: number;
  date: string;
}

interface StoreCreditEntry {
  id: number;
  amount: number;
  source_type: string;
  source_id: number | null;
  notes: string | null;
  created_at: string;
}

interface LedgerPurchaseReturn {
  id: number;
  number: string;
  po_id: number | null;
  po_number: string | null;
  date: string;
  status: string;
  total: number;
}

interface LedgerItemSupplied {
  item_id: number;
  code: string | null;
  name: string;
  status: string;
  preferred: boolean;
  qty_bought: number;
  last_cost: number | null;
  last_order_date: string | null;
}

interface LedgerTotals {
  total_purchased: number;
  total_paid: number;
  po_count: number;
}

interface LedgerResponse {
  data: {
    supplier: Supplier;
    purchase_orders: LedgerPO[];
    payments: LedgerPayment[];
    store_credit_balance: number;
    outstanding_balance: number;
    store_credit_entries: StoreCreditEntry[];
    purchase_returns: LedgerPurchaseReturn[];
    items_supplied: LedgerItemSupplied[];
    totals: LedgerTotals;
  };
}

interface StatementLine {
  date: string;
  type: 'PO' | 'Payment' | 'Purchase Return';
  ref: string;
  ref_kind: 'po' | 'payment' | 'purchase_return';
  ref_id: number;
  po_id: number | null;
  charge: number;
  credit: number;
  balance: number;
}

interface StatementResponse {
  data: {
    supplier: Supplier;
    from: string;
    to: string;
    opening_balance: number;
    closing_balance: number;
    total_charges: number;
    total_credits: number;
    lines: StatementLine[];
  };
}

// ─── Info field helper ─────────────────────────────────────────────────────────

function InfoField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-foreground">
        {value ?? <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

// ─── Format source type ────────────────────────────────────────────────────────

function formatSourceType(source_type: string): string {
  switch (source_type) {
    case 'purchase_return':
      return 'Purchase Return';
    case 'adjustment':
      return 'Adjustment';
    default:
      return source_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

// ─── Statement helpers ─────────────────────────────────────────────────────────

/** Jan 1 of the current year, as YYYY-MM-DD. */
function startOfYearISO(): string {
  return `${new Date().getFullYear()}-01-01`;
}

/** Today minus 12 months, as YYYY-MM-DD (local). */
function twelveMonthsAgoISO(): string {
  const now = new Date();
  return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toLocaleDateString('en-CA');
}

/** Earliest sensible "all-time" start for a statement range. */
const STATEMENT_ALL_FROM = '2000-01-01';

/** Route a statement line's Ref link, or null when there's no id to link to. */
function statementRefTo(line: StatementLine): string | null {
  if (line.ref_kind === 'purchase_return') {
    return line.ref_id != null ? `/purchase-returns/${line.ref_id}` : null;
  }
  // 'po' and 'payment' both point at the underlying purchase order.
  return line.po_id != null ? `/purchase-orders/${line.po_id}` : null;
}

// ─── PO status badge map ───────────────────────────────────────────────────────

const PO_STATUS_VARIANT: Record<string, 'gray' | 'blue' | 'green' | 'amber' | 'red'> = {
  draft: 'gray',
  sent: 'blue',
  received: 'green',
  returned: 'amber',
  cancelled: 'red',
};

// ─── Tab types ─────────────────────────────────────────────────────────────────

type Tab =
  | 'purchase-orders'
  | 'payments'
  | 'purchase-returns'
  | 'items-supplied'
  | 'supplier-credit'
  | 'statement';

const tabs: { label: string; value: Tab }[] = [
  { label: 'Purchase Orders', value: 'purchase-orders' },
  { label: 'Payments', value: 'payments' },
  { label: 'Purchase Returns', value: 'purchase-returns' },
  { label: 'Items Supplied', value: 'items-supplied' },
  { label: 'Supplier Credit', value: 'supplier-credit' },
  { label: 'Statement', value: 'statement' },
];

// ─── Sort accessors (stable module constants; one per table) ─────────────────────

const PO_ACCESSORS: Record<string, (r: LedgerPO) => SortValue> = {
  number: (r) => r.number,
  status: (r) => r.status,
  order_date: (r) => r.order_date,
  total: (r) => r.total,
  amount_paid: (r) => r.amount_paid,
  balance: (r) => r.total - r.amount_paid,
};

const PAYMENT_ACCESSORS: Record<string, (r: LedgerPayment) => SortValue> = {
  date: (r) => r.date,
  po_number: (r) => r.po_number,
  amount: (r) => r.amount,
};

const PURCHASE_RETURN_ACCESSORS: Record<string, (r: LedgerPurchaseReturn) => SortValue> = {
  date: (r) => r.date,
  number: (r) => r.number,
  po_number: (r) => r.po_number,
  status: (r) => r.status,
  total: (r) => r.total,
};

const ITEM_SUPPLIED_ACCESSORS: Record<string, (r: LedgerItemSupplied) => SortValue> = {
  name: (r) => r.name,
  last_cost: (r) => r.last_cost,
  last_order_date: (r) => r.last_order_date,
  qty_bought: (r) => r.qty_bought,
};

const STORE_CREDIT_ACCESSORS: Record<string, (r: StoreCreditEntry) => SortValue> = {
  created_at: (r) => r.created_at,
  source_type: (r) => r.source_type,
  amount: (r) => r.amount,
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function SupplierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('purchase-orders');

  const {
    data: supplierData,
    isLoading: supplierLoading,
    error: supplierError,
  } = useQuery({
    queryKey: ['supplier', id],
    queryFn: () => apiFetch<SupplierResponse>(`/api/suppliers/${id}`),
    enabled: !!id,
  });

  const { data: ledgerData, isLoading: ledgerLoading } = useQuery({
    queryKey: ['supplier-ledger', id],
    queryFn: () => apiFetch<LedgerResponse>(`/api/suppliers/${id}/ledger`),
    enabled: !!id,
  });

  // Statement tab: date-bounded running ledger. Defaults to Jan 1 → today.
  const [stmtFrom, setStmtFrom] = useState(startOfYearISO());
  const [stmtTo, setStmtTo] = useState(todayISO());
  const stmtDatesValid = Boolean(stmtFrom && stmtTo && stmtFrom <= stmtTo);

  const {
    data: statementData,
    isLoading: statementLoading,
    isError: statementIsError,
    error: statementError,
  } = useQuery({
    queryKey: ['supplier-statement', id, stmtFrom, stmtTo],
    queryFn: () =>
      apiFetch<StatementResponse>(
        `/api/suppliers/${id}/statement/ledger?from=${stmtFrom}&to=${stmtTo}`,
      ),
    enabled: !!id && stmtDatesValid,
  });

  // Client-side sorting per ledger table. Called unconditionally at the top of the
  // component (never inside an activeTab block) so hook order is stable across
  // renders; defaults preserve today's server-provided order. The Statement tab is
  // deliberately excluded — its running balance must stay in ledger order.
  const poSort = useClientSort(ledgerData?.data?.purchase_orders ?? [], {
    defaultKey: 'order_date',
    defaultDir: 'desc',
    accessors: PO_ACCESSORS,
  });
  const paymentSort = useClientSort(ledgerData?.data?.payments ?? [], {
    defaultKey: 'date',
    defaultDir: 'desc',
    accessors: PAYMENT_ACCESSORS,
  });
  const returnSort = useClientSort(ledgerData?.data?.purchase_returns ?? [], {
    defaultKey: 'date',
    defaultDir: 'desc',
    accessors: PURCHASE_RETURN_ACCESSORS,
  });
  const itemSort = useClientSort(ledgerData?.data?.items_supplied ?? [], {
    defaultKey: 'name',
    defaultDir: 'asc',
    accessors: ITEM_SUPPLIED_ACCESSORS,
  });
  const creditSort = useClientSort(ledgerData?.data?.store_credit_entries ?? [], {
    defaultKey: 'created_at',
    defaultDir: 'desc',
    accessors: STORE_CREDIT_ACCESSORS,
  });

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['supplier', id] });
    queryClient.invalidateQueries({ queryKey: ['supplier-ledger', id] });
    queryClient.invalidateQueries({ queryKey: ['suppliers'] });
  }

  if (supplierLoading) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (supplierError || !supplierData?.data.supplier) {
    return (
      <div className="p-6">
        <Link
          to="/suppliers"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Suppliers
        </Link>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {supplierError instanceof Error ? supplierError.message : 'Supplier not found.'}
        </div>
      </div>
    );
  }

  const supplier = supplierData.data.supplier;
  const ledger = ledgerData?.data;

  return (
    <div className="p-6 max-w-6xl">
      {/* Back nav */}
      <Link
        to="/suppliers"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Suppliers
      </Link>

      {/* Top row: supplier info (left) + account summary (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Info Card */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <CardTitle className="text-xl">{supplier.name}</CardTitle>
                {supplier.status === 'active' ? (
                  <Badge variant="green">Active</Badge>
                ) : (
                  <Badge variant="red">Inactive</Badge>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4">
              <InfoField label="Code" value={supplier.code} />
              <InfoField label="Contact Person" value={supplier.contact_person} />
              <InfoField label="Phone" value={supplier.phone} />
              <InfoField label="Email" value={supplier.email} />
              {supplier.address && (
                <div className="col-span-2 sm:col-span-3 space-y-0.5">
                  <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Address
                  </dt>
                  <dd className="text-sm text-foreground whitespace-pre-line">
                    {supplier.address}
                  </dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        {/* Account summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Account summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                Outstanding Balance
              </div>
              <div className="text-3xl font-semibold tabular-nums">
                {ledgerLoading ? '…' : formatCents(ledger?.outstanding_balance ?? 0)}
              </div>
            </div>
            <InfoField
              label="Store Credit"
              value={formatCents(ledger?.store_credit_balance ?? 0)}
            />
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t pt-3">
              <InfoField
                label="Total purchased"
                value={formatCents(ledger?.totals?.total_purchased ?? 0)}
              />
              <InfoField label="Total paid" value={formatCents(ledger?.totals?.total_paid ?? 0)} />
              <InfoField label="POs" value={ledger?.totals?.po_count ?? 0} />
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Ledger Tabs */}
      <div>
        {/* Tab bar */}
        <div className="flex border-b mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.value
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'purchase-orders' && (
          <div className="rounded-md border overflow-hidden">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !ledger?.purchase_orders?.length ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                No purchase orders yet — will appear once created.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/20">
                      <SortableTh
                        label="Number"
                        sortKey="number"
                        sort={poSort.sort}
                        dir={poSort.dir}
                        onToggle={poSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Status"
                        sortKey="status"
                        sort={poSort.sort}
                        dir={poSort.dir}
                        onToggle={poSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Order Date"
                        sortKey="order_date"
                        sort={poSort.sort}
                        dir={poSort.dir}
                        onToggle={poSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Total"
                        sortKey="total"
                        sort={poSort.sort}
                        dir={poSort.dir}
                        onToggle={poSort.toggleSort}
                        align="right"
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Paid"
                        sortKey="amount_paid"
                        sort={poSort.sort}
                        dir={poSort.dir}
                        onToggle={poSort.toggleSort}
                        align="right"
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Balance"
                        sortKey="balance"
                        sort={poSort.sort}
                        dir={poSort.dir}
                        onToggle={poSort.toggleSort}
                        align="right"
                        className="px-3 py-2.5 text-xs"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {poSort.sorted.map((po) => (
                      <tr
                        key={po.id}
                        className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-3 py-2.5">
                          <Link
                            to={`/purchase-orders/${po.id}`}
                            className="font-mono text-sm font-medium text-primary hover:underline"
                          >
                            {po.number}
                          </Link>
                        </td>
                        <td className="px-3 py-2.5">
                          <Badge variant={PO_STATUS_VARIANT[po.status] ?? 'gray'}>
                            {po.status.charAt(0).toUpperCase() + po.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="px-3 py-2.5 text-sm text-muted-foreground tabular-nums">
                          {formatDate(po.order_date)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm">
                          {formatCents(po.total)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm text-muted-foreground">
                          {formatCents(po.amount_paid)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                          {formatCents(po.total - po.amount_paid)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="rounded-md border overflow-hidden">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !ledger?.payments?.length ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                No supplier payments recorded yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/20">
                      <SortableTh
                        label="Date"
                        sortKey="date"
                        sort={paymentSort.sort}
                        dir={paymentSort.dir}
                        onToggle={paymentSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="PO #"
                        sortKey="po_number"
                        sort={paymentSort.sort}
                        dir={paymentSort.dir}
                        onToggle={paymentSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Amount"
                        sortKey="amount"
                        sort={paymentSort.sort}
                        dir={paymentSort.dir}
                        onToggle={paymentSort.toggleSort}
                        align="right"
                        className="px-3 py-2.5 text-xs"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {paymentSort.sorted.map((payment) => (
                      <tr
                        key={payment.id}
                        className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-3 py-2.5 text-sm text-muted-foreground tabular-nums">
                          {formatDate(payment.date)}
                        </td>
                        <td className="px-3 py-2.5">
                          <Link
                            to={`/purchase-orders/${payment.po_id}`}
                            className="font-mono text-sm font-medium text-primary hover:underline"
                          >
                            {payment.po_number}
                          </Link>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                          {formatCents(payment.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'purchase-returns' && (
          <div className="rounded-md border overflow-hidden">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !ledger?.purchase_returns?.length ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                No purchase returns yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/20">
                      <SortableTh
                        label="Date"
                        sortKey="date"
                        sort={returnSort.sort}
                        dir={returnSort.dir}
                        onToggle={returnSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="PR #"
                        sortKey="number"
                        sort={returnSort.sort}
                        dir={returnSort.dir}
                        onToggle={returnSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="PO #"
                        sortKey="po_number"
                        sort={returnSort.sort}
                        dir={returnSort.dir}
                        onToggle={returnSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Status"
                        sortKey="status"
                        sort={returnSort.sort}
                        dir={returnSort.dir}
                        onToggle={returnSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Amount"
                        sortKey="total"
                        sort={returnSort.sort}
                        dir={returnSort.dir}
                        onToggle={returnSort.toggleSort}
                        align="right"
                        className="px-3 py-2.5 text-xs"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {returnSort.sorted.map((pr) => (
                      <tr
                        key={pr.id}
                        className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-3 py-2.5 text-sm text-muted-foreground tabular-nums">
                          {formatDate(pr.date)}
                        </td>
                        <td className="px-3 py-2.5">
                          <Link
                            to={`/purchase-returns/${pr.id}`}
                            className="font-mono text-sm font-medium text-primary hover:underline"
                          >
                            {pr.number}
                          </Link>
                        </td>
                        <td className="px-3 py-2.5">
                          {pr.po_id != null ? (
                            <Link
                              to={`/purchase-orders/${pr.po_id}`}
                              className="font-mono text-sm font-medium text-primary hover:underline"
                            >
                              {pr.po_number}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <Badge variant={PO_STATUS_VARIANT[pr.status] ?? 'gray'}>
                            {pr.status.charAt(0).toUpperCase() + pr.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                          {formatCents(pr.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'items-supplied' && (
          <div className="rounded-md border overflow-hidden">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !ledger?.items_supplied?.length ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                No items purchased from this supplier yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/20">
                      <SortableTh
                        label="Item"
                        sortKey="name"
                        sort={itemSort.sort}
                        dir={itemSort.dir}
                        onToggle={itemSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Last cost"
                        sortKey="last_cost"
                        sort={itemSort.sort}
                        dir={itemSort.dir}
                        onToggle={itemSort.toggleSort}
                        align="right"
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Last order"
                        sortKey="last_order_date"
                        sort={itemSort.sort}
                        dir={itemSort.dir}
                        onToggle={itemSort.toggleSort}
                        className="px-3 py-2.5 text-xs"
                      />
                      <SortableTh
                        label="Qty bought"
                        sortKey="qty_bought"
                        sort={itemSort.sort}
                        dir={itemSort.dir}
                        onToggle={itemSort.toggleSort}
                        align="right"
                        className="px-3 py-2.5 text-xs"
                      />
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground" />
                    </tr>
                  </thead>
                  <tbody>
                    {itemSort.sorted.map((it) => (
                      <tr
                        key={it.item_id}
                        className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-3 py-2.5">
                          <Link
                            to={`/items/${it.item_id}`}
                            className="text-sm text-primary hover:underline"
                          >
                            {it.code && (
                              <span className="font-mono text-xs text-muted-foreground mr-2">
                                {it.code}
                              </span>
                            )}
                            {it.name}
                          </Link>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm">
                          {it.last_cost != null ? (
                            formatCents(it.last_cost)
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-sm text-muted-foreground tabular-nums">
                          {it.last_order_date ? formatDate(it.last_order_date) : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm">
                          {it.qty_bought}
                        </td>
                        <td className="px-3 py-2.5">
                          {it.preferred && <Badge variant="blue">Preferred</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'supplier-credit' && (
          <div className="rounded-md border">
            {ledgerLoading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : (
              <>
                {/* Balance header row */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/10">
                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                      Current Balance
                    </div>
                    <div className="text-2xl font-semibold tabular-nums">
                      {formatCents(ledger?.store_credit_balance ?? 0)}
                    </div>
                  </div>
                </div>

                {/* Ledger entries table */}
                {!ledger?.store_credit_entries?.length ? (
                  <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No supplier credit activity yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/20">
                          <SortableTh
                            label="Date"
                            sortKey="created_at"
                            sort={creditSort.sort}
                            dir={creditSort.dir}
                            onToggle={creditSort.toggleSort}
                            className="px-3 py-2.5 text-xs"
                          />
                          <SortableTh
                            label="Type"
                            sortKey="source_type"
                            sort={creditSort.sort}
                            dir={creditSort.dir}
                            onToggle={creditSort.toggleSort}
                            className="px-3 py-2.5 text-xs"
                          />
                          <SortableTh
                            label="Amount"
                            sortKey="amount"
                            sort={creditSort.sort}
                            dir={creditSort.dir}
                            onToggle={creditSort.toggleSort}
                            align="right"
                            className="px-3 py-2.5 text-xs"
                          />
                          <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                            Notes
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditSort.sorted.map((entry) => (
                          <tr
                            key={entry.id}
                            className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                          >
                            <td className="px-3 py-2.5 tabular-nums text-sm">
                              {formatDate(entry.created_at)}
                            </td>
                            <td className="px-3 py-2.5 text-sm">
                              {formatSourceType(entry.source_type)}
                            </td>
                            <td
                              className={`px-3 py-2.5 text-right tabular-nums text-sm font-medium ${
                                entry.amount >= 0
                                  ? 'text-green-700 dark:text-green-400'
                                  : 'text-destructive'
                              }`}
                            >
                              {entry.amount >= 0 ? '+' : ''}
                              {formatCents(entry.amount)}
                            </td>
                            <td className="px-3 py-2.5 text-sm text-muted-foreground">
                              {entry.notes ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'statement' && (
          <div className="space-y-4">
            {/* Controls: presets + date range (left) · exports (right) */}
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap items-end gap-3">
                {/* Quick presets */}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setStmtFrom(startOfYearISO());
                      setStmtTo(todayISO());
                    }}
                    className="px-2.5 py-1.5 rounded-md border border-input bg-background text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    This year
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStmtFrom(twelveMonthsAgoISO());
                      setStmtTo(todayISO());
                    }}
                    className="px-2.5 py-1.5 rounded-md border border-input bg-background text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    Last 12 months
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStmtFrom(STATEMENT_ALL_FROM);
                      setStmtTo(todayISO());
                    }}
                    className="px-2.5 py-1.5 rounded-md border border-input bg-background text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    All
                  </button>
                </div>

                {/* From / To */}
                <div className="flex items-center gap-2">
                  <div>
                    <Label
                      htmlFor="stmt-from"
                      className="text-xs text-muted-foreground mb-1.5 block"
                    >
                      From
                    </Label>
                    <Input
                      id="stmt-from"
                      type="date"
                      value={stmtFrom}
                      onChange={(e) => setStmtFrom(e.target.value)}
                      className="w-36 h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="stmt-to" className="text-xs text-muted-foreground mb-1.5 block">
                      To
                    </Label>
                    <Input
                      id="stmt-to"
                      type="date"
                      value={stmtTo}
                      onChange={(e) => setStmtTo(e.target.value)}
                      className="w-36 h-9 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Exports — reflect the current range */}
              <div className="flex items-center gap-1.5">
                <a
                  href={`/api/suppliers/${id}/statement/pdf?from=${stmtFrom}&to=${stmtTo}&download=1`}
                  download
                  className={linkClass}
                >
                  <Download className="h-3 w-3" />
                  Export PDF
                </a>
                <a
                  href={`/api/suppliers/${id}/statement/xlsx?from=${stmtFrom}&to=${stmtTo}`}
                  download
                  className={linkClass}
                >
                  <Download className="h-3 w-3" />
                  Export Excel
                </a>
              </div>
            </div>

            {/* Ledger */}
            {!stmtDatesValid ? (
              <p className="text-sm text-destructive">
                &ldquo;From&rdquo; must be on or before &ldquo;To&rdquo;.
              </p>
            ) : (
              <div className="rounded-md border overflow-hidden">
                {statementLoading ? (
                  <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Loading…
                  </div>
                ) : statementIsError ? (
                  <div className="px-4 py-8 text-center text-sm text-destructive">
                    {statementError instanceof Error
                      ? statementError.message
                      : 'Failed to load statement.'}
                  </div>
                ) : statementData?.data ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/20">
                          <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                            Date
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                            Type
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                            Ref
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                            Charge
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                            Credit
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                            Balance
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Opening balance */}
                        <tr className="border-b bg-muted/10">
                          <td
                            colSpan={5}
                            className="px-3 py-2.5 text-sm font-medium text-muted-foreground"
                          >
                            Opening balance (as of {formatDate(statementData.data.from)})
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                            {formatCents(statementData.data.opening_balance)}
                          </td>
                        </tr>

                        {/* Transactions */}
                        {statementData.data.lines.length === 0 ? (
                          <tr className="border-b">
                            <td
                              colSpan={6}
                              className="px-4 py-10 text-center text-sm text-muted-foreground"
                            >
                              No transactions in this range.
                            </td>
                          </tr>
                        ) : (
                          statementData.data.lines.map((line, i) => {
                            const to = statementRefTo(line);
                            return (
                              <tr
                                key={`${line.ref_kind}-${line.ref_id}-${i}`}
                                className="border-b hover:bg-muted/20 transition-colors"
                              >
                                <td className="px-3 py-2.5 text-sm text-muted-foreground tabular-nums">
                                  {formatDate(line.date)}
                                </td>
                                <td className="px-3 py-2.5 text-sm">{line.type}</td>
                                <td className="px-3 py-2.5">
                                  {to ? (
                                    <Link
                                      to={to}
                                      className="font-mono text-sm font-medium text-primary hover:underline"
                                    >
                                      {line.ref}
                                    </Link>
                                  ) : (
                                    <span className="font-mono text-sm text-muted-foreground">
                                      {line.ref}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-right tabular-nums text-sm">
                                  {line.charge ? formatCents(line.charge) : ''}
                                </td>
                                <td className="px-3 py-2.5 text-right tabular-nums text-sm">
                                  {line.credit ? formatCents(line.credit) : ''}
                                </td>
                                <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                                  {formatCents(line.balance)}
                                </td>
                              </tr>
                            );
                          })
                        )}

                        {/* Closing balance */}
                        <tr className="border-b bg-muted/20">
                          <td colSpan={5} className="px-3 py-2.5 text-sm font-semibold">
                            Closing balance (as of {formatDate(statementData.data.to)})
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-semibold">
                            {formatCents(statementData.data.closing_balance)}
                          </td>
                        </tr>

                        {/* Totals */}
                        <tr className="bg-muted/10">
                          <td
                            colSpan={3}
                            className="px-3 py-2.5 text-sm font-medium text-muted-foreground"
                          >
                            Totals
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-semibold">
                            {formatCents(statementData.data.total_charges)}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-semibold">
                            {formatCents(statementData.data.total_credits)}
                          </td>
                          <td className="px-3 py-2.5" />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editOpen && (
        <SupplierEditModal
          supplier={supplier}
          onClose={() => setEditOpen(false)}
          onSaved={refetch}
        />
      )}
    </div>
  );
}
